"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";

const PORTAL_HOST_STYLE_TEXT =
  "display: flex; flex-direction: column; flex: 1 1 auto; width: 100%; height: 100%; min-height: 0; position: relative; outline: none;";

interface UseCmarkdownvisualPortalInput {
  isModalOpen: boolean;
}

interface UseCmarkdownvisualPortalResult {
  keyboardWrapperRef: RefObject<HTMLDivElement | null>;
  portalHost: HTMLDivElement | null;
  setCanvasSlot: Dispatch<SetStateAction<HTMLDivElement | null>>;
  setModalSlot: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

interface MovePortalHostInput {
  canvasSlot: HTMLDivElement | null;
  isModalOpen: boolean;
  modalSlot: HTMLDivElement | null;
  portalHost: HTMLDivElement | null;
}

function getPortalTarget({
  canvasSlot,
  isModalOpen,
  modalSlot,
}: Omit<MovePortalHostInput, "portalHost">): HTMLDivElement | null {
  if (isModalOpen) {
    return modalSlot;
  }

  return canvasSlot;
}

function movePortalHost({
  canvasSlot,
  isModalOpen,
  modalSlot,
  portalHost,
}: MovePortalHostInput): void {
  if (!portalHost) {
    return;
  }

  const target = getPortalTarget({ canvasSlot, isModalOpen, modalSlot });

  if (!target) {
    return;
  }

  if (portalHost.parentNode === target) {
    return;
  }

  target.appendChild(portalHost);
}

export function useCmarkdownvisualPortal({
  isModalOpen,
}: UseCmarkdownvisualPortalInput): UseCmarkdownvisualPortalResult {
  const keyboardWrapperRef = useRef<HTMLDivElement | null>(null);
  const [portalHost] = useState<HTMLDivElement | null>(() => {
    if (typeof document === "undefined") {
      return null;
    }

    const host = document.createElement("div");
    host.style.cssText = PORTAL_HOST_STYLE_TEXT;
    return host;
  });
  const [canvasSlot, setCanvasSlot] = useState<HTMLDivElement | null>(null);
  const [modalSlot, setModalSlot] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    movePortalHost({ canvasSlot, isModalOpen, modalSlot, portalHost });
  }, [isModalOpen, canvasSlot, modalSlot, portalHost]);

  useLayoutEffect(() => {
    if (!isModalOpen || typeof window === "undefined") {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      keyboardWrapperRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isModalOpen]);

  return {
    keyboardWrapperRef,
    portalHost,
    setCanvasSlot,
    setModalSlot,
  };
}
